// frontend/src/apiService.js
// Serviço centralizado para todas as chamadas API com JWT

import i18n from './i18n';

const API_URL = '/api';

class ApiService {
  constructor() {
    this.accessToken = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
  }

  // ==================== TOKEN MANAGEMENT ====================
  
  setTokens(accessToken, refreshToken) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  getAccessToken() {
    return this.accessToken || localStorage.getItem('access_token');
  }

  isAuthenticated() {
    return !!this.getAccessToken();
  }

  // ==================== HTTP REQUEST ====================
  
  async request(url, options = {}) {
    const token = this.getAccessToken();
    
    // Preparar headers
    const headers = {
      ...options.headers,
    };

    // Adicionar token se existir
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Se for JSON, adicionar Content-Type
    if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(options.body);
    }

    try {
      const response = await fetch(`${API_URL}${url}`, {
        ...options,
        headers
      });

      // Token expirado - tentar refresh
      if (response.status === 401 && this.refreshToken) {
        console.log('🔄 Token expirado, tentando refresh...');
        
        const refreshed = await this.refreshAccessToken();
        
        if (refreshed) {
          // Retry request com novo token
          headers['Authorization'] = `Bearer ${this.accessToken}`;
          return await fetch(`${API_URL}${url}`, {
            ...options,
            headers
          });
        } else {
          // Refresh falhou, fazer logout
          this.clearTokens();
          window.location.reload();
          throw new Error(i18n.t('erros.sessaoExpirada'));
        }
      }

      return response;

    } catch (error) {
      console.error('API Request Error:', error);
      throw error;
    }
  }

  // ==================== TOKEN REFRESH ====================
  
  async refreshAccessToken() {
    try {
      const formData = new FormData();
      formData.append('refresh_token', this.refreshToken);

      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        this.accessToken = data.access_token;
        localStorage.setItem('access_token', data.access_token);
        console.log('✅ Token refreshed successfully');
        return true;
      }

      return false;

    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  // ==================== AUTHENTICATION ====================
  
  async login(username, password) {
  const response = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      username: username,
      password: password
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || i18n.t('erros.loginFalhou'));
  }

  const data = await response.json();

  // Desde a v5.13 a password certa já não dá tokens: dá um desafio de MFA
  // ({ mfa_required } ou { mfa_setup_required }), e os tokens só chegam
  // depois do segundo fator. Só se guardam se vierem.
  if (data.access_token) {
    this.setTokens(data.access_token, data.refresh_token);
  }

  return data;
}

  // ------------------------------------------------------------------ MFA
  //
  // Os três pedidos do segundo passo do login. Não passam por this.request
  // porque ainda não há sessão: o que os autoriza é o identificador do
  // desafio, emitido pelo login depois da password certa.

  async _mfaPost(caminho, corpo) {
    const response = await fetch(`${API_URL}/auth/mfa/${caminho}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(corpo),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const erro = new Error(data.detail || i18n.t('mfa.erroGenerico'));
      // O desafio morreu (expirou, esgotou as tentativas, conta desativada):
      // o ecrã tem de voltar à password. O backend diz isso num cabeçalho,
      // em vez de o frontend ter de interpretar o texto da mensagem.
      erro.reiniciar = response.headers.get('X-MFA-Restart') === '1';
      throw erro;
    }
    if (data.access_token) {
      this.setTokens(data.access_token, data.refresh_token);
    }
    return data;
  }

  mfaVerify(challenge, code) {
    return this._mfaPost('verify', { challenge, code });
  }

  mfaSetupStart(challenge) {
    return this._mfaPost('setup/start', { challenge });
  }

  mfaSetupConfirm(challenge, code) {
    return this._mfaPost('setup/confirm', { challenge, code });
  }

  logout() {
    this.clearTokens();
    window.location.href = '/';
  }

  // ==================== API ENDPOINTS ====================

  // MELCloud - Dispositivos AC
  async getDevices() {
    const response = await this.request('/devices');
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return await response.json();
  }

  async controlDevice(deviceId, buildingId, command) {
    const response = await this.request(
      `/devices/${deviceId}/${buildingId}/control`,
      {
        method: 'POST',
        body: command
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error);
    }

    return await response.json();
  }

  // Câmaras
  async getCameraSnapshot(cameraId) {
    const response = await this.request(`/cameras/${cameraId}/snapshot?t=${Date.now()}`);
    
    if (!response.ok) {
      throw new Error(i18n.t('erros.carregarSnapshot'));
    }

    return await response.blob();
  }

  async controlCamera(cameraId, direction) {
    const response = await this.request(`/cameras/${cameraId}/ptz`, {
      method: 'POST',
      body: { direction }
    });

    return response.ok;
  }

  async configureCameras(cameras) {
    const promises = cameras.map(camera =>
      this.request('/cameras/config', {
        method: 'POST',
        body: camera
      })
    );

    await Promise.all(promises);
  }

  // Estores (Shelly)
  async getShellyDevices() {
    const response = await this.request('/shelly/devices');
    if (!response.ok) {
      throw new Error(i18n.t('erros.buscarEstores'));
    }
    return await response.json();
  }

  async controlShelly(deviceId, command) {
    const response = await this.request(`/shelly/${deviceId}/control`, {
      method: 'POST',
      body: command
    });

    return response.ok;
  }

  async configureShelly(devices) {
    const promises = devices.map(device =>
      this.request('/shelly/config', {
        method: 'POST',
        body: device
      })
    );

    await Promise.all(promises);
  }

  // Tuya (Bomba + VMC)
  async getTuyaDevices() {
    const response = await this.request('/tuya/devices');
    if (!response.ok) {
      throw new Error(i18n.t('erros.buscarTuya'));
    }
    return await response.json();
  }

  async controlTuyaDevice(deviceId, command) {
    const response = await this.request(`/tuya/${deviceId}/control`, {
      method: 'POST',
      body: command
    });

    return response.ok;
  }

  async configureTuya(devices) {
    const promises = devices.map(device =>
      this.request('/tuya/config', {
        method: 'POST',
        body: device
      })
    );

    await Promise.all(promises);
  }

  // Health Check
  async healthCheck() {
    const response = await fetch(`${API_URL}/health`);
    return await response.json();
  }
}

// Exportar instância única (singleton)
const apiService = new ApiService();
export default apiService;
